import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { discography } from "../data/discography";
import { musicVideos } from "../data/musicVideos";

const Music = () => {
  const { t, i18n } = useTranslation();
  const [featuredVideoId, setFeaturedVideoId] = useState(musicVideos[0].id);
  const [activeTab, setActiveTab] = useState("spotify");
  const [lyricsModal, setLyricsModal] = useState({
    isOpen: false,
    content: "",
  });
  const [isExpanded, setIsExpanded] = useState(false);

  const initialLimit = 3;

  const openLyricsModal = (lyricsKey) => {
    if (i18n.exists(lyricsKey)) {
      setLyricsModal({ isOpen: true, content: t(lyricsKey) });
    }
  };

  const closeLyricsModal = () => {
    setLyricsModal({ isOpen: false, content: "" });
  };

  const displayedDiscography = isExpanded
    ? discography
    : discography.slice(0, initialLimit);

  return (
    <main className="page-content">
      {/* Lyrics Modal */}
      {lyricsModal.isOpen && (
        <div className="lyrics-modal-overlay" onClick={closeLyricsModal}>
          <div
            className="lyrics-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="close-modal-btn" onClick={closeLyricsModal}>
              ×
            </button>
            <pre>{lyricsModal.content}</pre>
          </div>
        </div>
      )}

      <div className="page-title-container">
        <h1>{t("musicTitle")}</h1>
      </div>

      {/* Interactive Video Section */}
      <section className="music-page-section">
        <h2>{t("videosSectionTitle")}</h2>
        <div className="featured-video-container">
          <iframe
            key={featuredVideoId}
            src={`https://www.youtube.com/embed/${featuredVideoId}`}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </div>
        <div className="video-thumbnail-gallery">
          {musicVideos.map((video) => (
            <div
              key={video.id}
              className={`thumbnail-item ${
                featuredVideoId === video.id ? "active" : ""
              }`}
              onClick={() => setFeaturedVideoId(video.id)}
            >
              <img
                src={`https://img.youtube.com/vi/${video.id}/mqdefault.jpg`}
                alt={video.title}
              />
              <div className="thumbnail-play-icon">▶</div>
            </div>
          ))}
        </div>
      </section>

      {/* Discography Section */}
      <section className="music-page-section">
        <h2>{t("discographySectionTitle")}</h2>
        <div className="discography-grid">
          {displayedDiscography.map((item) => {
            const title = t(`discography.${item.key}.title`);
            const lyricsKey = `discography.${item.key}.lyrics`;

            return (
              <div key={item.id} className="discography-item">
                <img src={item.imageUrl} alt={title} className="album-art" />
                <div className="item-details">
                  <h3>{title}</h3>
                  <p>
                    {t(item.typeKey)} • {item.year}
                  </p>
                  <div className="discography-buttons">
                    <a
                      href={item.links.primary || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cta-button"
                    >
                      {t("listenNow")}
                    </a>
                    {i18n.exists(lyricsKey) && (
                      <button
                        onClick={() => openLyricsModal(lyricsKey)}
                        className="secondary-button"
                      >
                        {t("viewLyrics")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {/* UPDATED: Show More/Less Toggle Button */}
        {discography.length > initialLimit && (
          <div className="show-more-container">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="show-more-btn"
            >
              {isExpanded ? t("showLess") : t("showMore")}
            </button>
          </div>
        )}
      </section>

      {/* UPDATED: Tabbed Streaming Player with Your Embeds */}
      <section className="music-page-section">
        <h2>{t("listenOnSectionTitle")}</h2>
        <div className="streaming-player-container">
          <div className="streaming-tabs">
            <button
              className={activeTab === "spotify" ? "active" : ""}
              onClick={() => setActiveTab("spotify")}
            >
              Spotify
            </button>
            <button
              className={activeTab === "apple" ? "active" : ""}
              onClick={() => setActiveTab("apple")}
            >
              Apple Music
            </button>
            <button
              className={activeTab === "amazon" ? "active" : ""}
              onClick={() => setActiveTab("amazon")}
            >
              Amazon Music
            </button>
            <button
              className={activeTab === "deezer" ? "active" : ""}
              onClick={() => setActiveTab("deezer")}
            >
              Deezer
            </button>
          </div>
          <div className="streaming-content">
            {activeTab === "spotify" && (
              <iframe
                data-testid="embed-iframe"
                style={{ borderRadius: "12px" }}
                title="Spotify Player"
                src="https://open.spotify.com/embed/artist/3TOwpeyQ7buBJq6ws5xKMk?utm_source=generator"
                width="100%"
                height="352"
                frameBorder="0"
                allowFullScreen=""
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
              ></iframe>
            )}
            {activeTab === "apple" && (
              <iframe
                title="Apple Music Player"
                allow="autoplay *; encrypted-media *;"
                frameBorder="0"
                height="450"
                style={{
                  width: "100%",
                  maxWidth: "660px",
                  overflow: "hidden",
                  background: "transparent",
                }}
                sandbox="allow-forms allow-popups allow-same-origin allow-scripts allow-storage-access-by-user-activation allow-top-navigation-by-user-activation"
                src="https://embed.music.apple.com/us/artist/marino-flores-y-su-estilo-sierre%C3%B1o/1648413464"
              ></iframe>
            )}
            {activeTab === "amazon" && (
              <iframe
                id="AmazonMusicEmbedB0BHBY7R4K"
                title="Amazon Music Player"
                src="https://music.amazon.com/embed/B0BHBY7R4K/?id=jsAmmtoVuV&marketplaceId=ATVPDKIKX0DER&musicTerritory=US"
                width="100%"
                height="352px"
                frameBorder="0"
                style={{ borderRadius: "20px" }}
              ></iframe>
            )}
            {activeTab === "deezer" && (
              <iframe
                title="deezer-widget"
                src="https://widget.deezer.com/widget/dark/artist/185619897/top_tracks"
                width="100%"
                height="300"
                frameborder="0"
                allowtransparency="true"
                allow="encrypted-media; clipboard-write"
              ></iframe>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Music;
